import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const EmployeeHelpResources = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const helpSections = [
    {
      id: 'access',
      title: 'First Time Access',
      icon: 'UserPlus',
      content: [
        'Use your work email address to log in',
        'Your initial password was provided by HR during onboarding',
        'Contact your HR department if you need password assistance',
        'Multi-factor authentication may be required for enhanced security'
      ]
    },
    {
      id: 'data',
      title: 'Your Compensation Data',
      icon: 'Database',
      content: [
        'View your current pay band and quartile position',
        'Access historical compensation data and progression',
        'Compare with anonymized peer data in similar roles',
        'Request additional compensation information through secure forms'
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: 'Lock',
      content: [
        'Your data is encrypted and GDPR compliant',
        'Only you can access your personal compensation information',
        'All access is logged for security and audit purposes',
        'Report any security concerns immediately to IT support'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="w-full">
      {/* Quick Help Toggle */}
      <div className="mb-4">
        <button 
          onClick={() => setExpandedSection(expandedSection ? null : 'help')}
          className="flex items-center space-x-2 text-sm text-emerald-600 hover:text-emerald-700 transition-smooth"
        >
          <Icon name="HelpCircle" size={16} />
          <span>Need help getting started?</span>
          <Icon 
            name={expandedSection ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
            className="transition-transform duration-200"
          />
        </button>
      </div>

      {/* Expandable Help Sections */}
      {expandedSection && (
        <div className="space-y-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          {helpSections?.map((section) => (
            <div key={section?.id} className="border-b border-emerald-200 last:border-b-0 pb-3 last:pb-0">
              <button
                onClick={() => toggleSection(section?.id)}
                className="flex items-center justify-between w-full text-left py-2 text-emerald-800 hover:text-emerald-900 transition-smooth"
              >
                <div className="flex items-center space-x-2">
                  <Icon name={section?.icon} size={16} className="text-emerald-600" />
                  <span className="font-medium text-sm">{section?.title}</span>
                </div>
                <Icon 
                  name={expandedSection === section?.id ? 'Minus' : 'Plus'} 
                  size={14} 
                  className="text-emerald-600"
                />
              </button>
              
              {expandedSection === section?.id && (
                <div className="mt-2 ml-6 space-y-1">
                  {section?.content?.map((item, index) => (
                    <p key={index} className="text-xs text-emerald-700 leading-relaxed">
                      • {item}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Contact Information */}
          <div className="pt-3 border-t border-emerald-200">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center space-x-2 text-emerald-700">
                <Icon name="Phone" size={12} />
                <span>HR: ext. 1234</span>
              </div>
              <div className="flex items-center space-x-2 text-emerald-700">
                <Icon name="Mail" size={12} />
                <span>support@company.eu</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Onboarding Guide */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start space-x-2">
          <Icon name="BookOpen" size={16} className="text-blue-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800 mb-1">Employee Guide</p>
            <p className="text-blue-700 text-xs">
              New to pay transparency? Access our comprehensive employee guide for understanding your compensation data and rights.
            </p>
            <button className="mt-2 text-xs text-blue-600 hover:text-blue-700 transition-smooth font-medium">
              View Employee Guide →
            </button>
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="mt-4 text-center">
        <p className="text-xs text-muted-foreground mb-2">
          Need immediate assistance?
        </p>
        <div className="flex items-center justify-center space-x-4 text-xs">
          <button className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 transition-smooth">
            <Icon name="MessageCircle" size={12} />
            <span>Live Chat</span>
          </button>
          <span className="text-muted-foreground">•</span>
          <button className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 transition-smooth">
            <Icon name="Phone" size={12} />
            <span>Call HR</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeHelpResources;